import { useState, useEffect } from 'react';
import { Table, Input, Button, message, Tag, Modal, Space, Select } from 'antd';
const { TextArea } = Input;
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = import.meta.env.VITE_API_BASE || '/api';
const h = () => ({ Authorization: 'Bearer ' + localStorage.getItem('ar_token') });

// 审核状态显示：优先看 submit_status，auth_status 只在已审核时用
const getStatusTag = (r) => {
  if (r.submit_status !== 'submitted') {
    return <Tag color="default">待提交</Tag>;
  }
  const map = {
    pending: { color: 'processing', text: '待审核' },
    submitted: { color: 'warning', text: '待审核' },
    passed: { color: 'success', text: '已通过' },
    rejected: { color: 'error', text: '已驳回' },
  };
  const s = map[r.auth_status] || { color: 'default', text: r.auth_status };
  return <Tag color={s.color}>{s.text}</Tag>;
};

export default function ReviewManage() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [reviewModal, setReviewModal] = useState({ open: false, letter: null, action: '', reason: '' });
  const [signerViewModal, setSignerViewModal] = useState({ open: false, cCusCode: '', ccusname: '' });
  const [viewSigners, setViewSigners] = useState([]);

  const fetchLetters = async () => {
    setLoading(true);
    try {
      const r = await axios.get(API + '/admin/review/auth-letters', {
        headers: h(), params: { keyword, status },
      });
      if (r.data.success) setLetters(r.data.data || []);
    } catch { message.error('加载委托书失败'); }
    setLoading(false);
  };

  const viewCustomerSigners = async (cCusCode, ccusname) => {
    try {
      const r = await axios.get(API + `/admin/customer/${cCusCode}/signers`, { headers: h() });
      setViewSigners(r.data.data || []);
      setSignerViewModal({ open: true, cCusCode, ccusname });
    } catch { message.error('加载签收人失败'); }
  };

  useEffect(() => { fetchLetters(); }, []);

  /** 通用的审核 API 调用（用于直接调用） */
  const doReview = async (letter, action, reason = '') => {
    try {
      await axios.post(API + `/admin/auth-letter/${letter.id}/review`, {
        status: action, reason,
      }, { headers: h() });
      message.success(action === 'passed' ? '已通过' : '已驳回');
      fetchLetters();
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.response?.data?.message || '操作失败';
      if (e?.response?.status === 400) {
        // 尝试解析后端返回的缺失材料列表
        const m = errMsg.match(/以下材料缺失：(.+?)。请补齐/);
        if (m) {
          const items = m[1].split('；');
          Modal.warning({
            title: '材料缺失，无法通过',
            content: (
              <div style={{ marginTop: 8 }}>
                {items.map((item, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>❌ {item}</div>
                ))}
              </div>
            ),
          });
        } else {
          Modal.warning({ title: '无法审核', content: errMsg });
        }
      } else {
        message.error(errMsg);
      }
    }
  };

  /** 直接通过（无需弹窗确认） */
  const handleDirectPass = (letter) => {
    doReview(letter, 'passed');
  };

  /** 驳回（保留确认弹窗） */
  const handleRejectClick = (letter) => {
    setReviewModal({ open: true, letter, action: 'rejected', reason: '' });
  };

  /** 驳回弹窗确认 */
  const handleReview = async () => {
    const { letter, action, reason } = reviewModal;
    if (!reason || !reason.trim()) {
      return message.warning('请填写驳回原因');
    }
    await doReview(letter, action, reason);
    setReviewModal({ open: false, letter: null, action: '', reason: '' });
  };

  const previewImg = (path) => {
    if (!path) return message.info('暂无图片');
    const url = '/' + path.replace(/\\/g, '/');
    const isPDF = path.toLowerCase().endsWith('.pdf');
    Modal.info({
      title: '预览',
      width: 800,
      content: isPDF
        ? <iframe src={url} style={{ width: '100%', height: '70vh', border: 'none' }} title="PDF预览" />
        : <img src={url} style={{ maxWidth: '100%' }} alt="预览" />,
    });
  };

  return (
    <div>
      {/* ========== 委托书管理 ========== */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Input placeholder="客户名称 / 客户编码" value={keyword}
          onChange={e => setKeyword(e.target.value)} onPressEnter={fetchLetters}
          style={{ width: 240 }} prefix={<SearchOutlined />} allowClear />
        <Select placeholder="提交状态" value={status || undefined}
          onChange={v => setStatus(v || '')} style={{ width: 130 }} allowClear
          options={[
            { value: 'not_submitted', label: '待提交' },
            { value: 'submitted', label: '待审核' },
            { value: 'passed', label: '已通过' },
            { value: 'rejected', label: '已驳回' },
          ]} />
        <Button type="primary" onClick={fetchLetters} icon={<SearchOutlined />}>查询</Button>
        <Button onClick={() => { setKeyword(''); setStatus(''); setTimeout(fetchLetters, 0); }}
          icon={<ReloadOutlined />}>刷新</Button>
      </Space>

      <Table dataSource={letters} loading={loading} rowKey="id" size="small"
        pagination={{ pageSize: 15, showTotal: t => `共 ${t} 条` }}>
        <Table.Column title="客户名称" dataIndex="ccusname" width={180} ellipsis />
        <Table.Column title="客户编码" dataIndex="cCusCode" width={120} />
        <Table.Column title="类型" width={80}
          render={(_, r) => r.is_enterprise ? <Tag color="blue">企业认证</Tag> : <Tag>委托书</Tag>} />
        <Table.Column title="提交状态" width={90}
          render={(_, r) => getStatusTag(r)} />
        <Table.Column title="委托书" width={80}
          render={(_, r) => (
            <Button type="link" size="small" icon={<EyeOutlined />}
              onClick={() => previewImg(r.letter_img)}>预览</Button>
          )} />
        <Table.Column title="操作" width={280}
          render={(_, r) => (
            <Space size={0}>
              <Button type="link" size="small" icon={<EyeOutlined />}
                onClick={() => viewCustomerSigners(r.cCusCode, r.ccusname)}>签收人</Button>
              {r.is_enterprise ? (
                <Tag>企业认证</Tag>
              ) : r.submit_status === 'submitted' && r.auth_status === 'submitted' ? (
                <Space size={0}>
                  <Button type="link" size="small" icon={<CheckCircleOutlined />} style={{ color: '#52c41a' }}
                    onClick={() => handleDirectPass(r)}>通过</Button>
                  <Button type="link" size="small" danger icon={<CloseCircleOutlined />}
                    onClick={() => handleRejectClick(r)}>驳回</Button>
                </Space>
              ) : (
                getStatusTag(r)
              )}
            </Space>
          )} />
      </Table>

      {/* 驳回确认弹窗（仅驳回用，通过无弹窗） */}
      <Modal title="确认驳回"
        open={reviewModal.open && reviewModal.action === 'rejected'}
        onOk={handleReview}
        onCancel={() => setReviewModal({ open: false, letter: null, action: '', reason: '' })}
        okText="确认驳回" cancelText="取消"
        okButtonProps={{ danger: true }}>
        <div style={{ marginBottom: 12 }}>
          确认驳回「<b>{reviewModal.letter?.ccusname}</b>」的委托书？驳回后客户需重新提交。
        </div>
        <div>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>驳回原因 <span style={{ color: '#ff4d4f' }}>*</span></div>
          <TextArea
            placeholder="请填写驳回原因，客户将看到此内容"
            value={reviewModal.reason}
            onChange={(e) => setReviewModal(m => ({ ...m, reason: e.target.value }))}
            rows={3}
            maxLength={200}
            showCount
          />
        </div>
      </Modal>

      {/* 签收人查看弹窗 */}
      <Modal title={`签收人列表 - ${signerViewModal.ccusname}`}
        open={signerViewModal.open}
        onCancel={() => setSignerViewModal({ open: false, cCusCode: '', ccusname: '' })}
        footer={<Button onClick={() => setSignerViewModal({ open: false, cCusCode: '', ccusname: '' })}>关闭</Button>}
        width={700}>
        <Table dataSource={viewSigners} rowKey="id" size="small" pagination={false}>
          <Table.Column title="姓名" dataIndex="signer_name" width={80} />
          <Table.Column title="手机" dataIndex="phone" width={120} />
          <Table.Column title="签名图片" width={90}
            render={(_, r) => r.seal_img ? (
              <img
                src={API.replace('/api', '') + '/' + r.seal_img.replace(/\\/g, '/')}
                style={{ width: 48, height: 48, objectFit: 'contain', cursor: 'pointer', border: '1px solid #f0f0f0', borderRadius: 4 }}
                onClick={() => previewImg(r.seal_img)}
                alt="签名"
              />
            ) : <Tag color="default">无</Tag>} />
          <Table.Column title="爱签账号" dataIndex="asign_account" width={120}
            render={v => v || <Tag color="default">未认证</Tag>} />
          <Table.Column title="爱签印章" dataIndex="asign_seal_no" width={140} ellipsis
            render={v => v || <Tag color="default">未同步</Tag>} />
          <Table.Column title="状态" width={70}
            render={(_, r) => r.is_active ? <Tag color="green">启用</Tag> : <Tag color="red">停用</Tag>} />
        </Table>
      </Modal>
    </div>
  );
}
